'use client';

import EmptyPlaceholder from '@/components/placeholders/empty-placeholder';
import {
  CustomerViewModel,
  LoginMethod,
} from '@hidayahlab/academy-suite-corelib';
import { Button, useToast } from '@tajdid/tajdid-ui';
import { useTranslations } from 'next-intl';

import { useRouter } from '@/i18n/routing';
import { Mail01Icon } from 'hugeicons-react';
import { useRef, useState } from 'react';
import LoginWithOtp from './login-with-otp';
import { MagicLinkForm } from './magic-link-form';
import { SuccessState } from './success-state';

type LoginFormProps = {
  loginMethods?: CustomerViewModel['loginMethods'];
  errorType?: string;
};

export default function LoginForm({
  loginMethods = [],
  errorType,
}: LoginFormProps) {
  const enabledMethods = loginMethods?.filter(method => method.isEnabled) ?? [];
  const hasMagicLink = enabledMethods?.some(
    method => method.name === LoginMethod.magicLink,
  );
  const hasOtp = enabledMethods?.some(
    method =>
      method.name === LoginMethod.sms || method.name === LoginMethod.mockSms,
  );

  const [activeMethod, setActiveMethod] = useState<'magic' | 'otp'>(
    hasOtp ? 'otp' : 'magic',
  );
  const [isSuccess, setIsSuccess] = useState(false);
  const [otpStep, setOtpStep] = useState(1);
  const t = useTranslations();
  const { showToast } = useToast();
  const toastShownRef = useRef(false);
  const router = useRouter();

  if (errorType === 'unauthorized' && !toastShownRef.current) {
    toastShownRef.current = true;
    showToast({
      title: 'Authorization Error',
      description:
        'You are not authorized to log in to the User App with this account',
      variant: 'error',
    });

    setTimeout(() => {
      router.replace('/login');
    }, 100);
  }

  const handleSuccess = () => {
    setIsSuccess(true);
  };

  const handleBack = () => {
    setIsSuccess(false);
  };

  if (!loginMethods || loginMethods.length === 0) {
    return (
      <EmptyPlaceholder title="No login methods are available. Please contact support." />
    );
  }

  if (!hasMagicLink && !hasOtp) {
    return (
      <EmptyPlaceholder title="No supported login methods are enabled. Please contact support." />
    );
  }

  if (!hasMagicLink && hasOtp) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="grid items-center py-10">
          <div className="bg-white w-full lg:w-[600px] p-6">
            <LoginWithOtp />
          </div>
        </div>
      </div>
    );
  }

  if (hasMagicLink && !hasOtp) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="grid items-center py-10">
          <div className="bg-white w-full lg:w-[600px] p-6">
            <MagicLinkForm
              onSuccess={handleSuccess}
              hasOtp={false}
              setActiveMethod={() => {}}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <div className="grid items-center py-10">
        <div className="bg-white w-full lg:w-[600px] p-6">
          {isSuccess ? (
            <SuccessState onBack={handleBack} />
          ) : activeMethod === 'otp' ? (
            <>
              <LoginWithOtp onStepChange={setOtpStep} />
              {hasMagicLink && otpStep === 1 && (
                <div className="max-w-md px-4 mx-auto mt-4 text-center">
                  <div className="relative flex items-center justify-center my-3">
                    <div className="absolute w-full border-t border-gray-300"></div>
                    <span className="relative px-4 text-gray-500 bg-white text-md">
                      Or continue with
                    </span>
                  </div>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setActiveMethod('magic')}
                    className="w-full mt-2"
                    icon={<Mail01Icon className="w-5 h-5" />}
                    iconPosition="start"
                  >
                    {t('profile-page.email')}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <MagicLinkForm
              onSuccess={handleSuccess}
              hasOtp={hasOtp}
              setActiveMethod={setActiveMethod}
            />
          )}
        </div>
      </div>
    </div>
  );
}
